import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { isProduction } from "@/lib/platform";
import TopUpModal from "@/components/TopUpModal";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import GoogleCallback from "@/pages/GoogleCallback";
import SSOCallback from "@/pages/SSOCallback";
import BuildStart from "@/pages/BuildStart";
import Dashboard from "@/pages/Dashboard";
import FinModelNew from "@/pages/FinModelNew";
import FinModelBuilder from "@/pages/FinModelBuilder";
import FinModelInterview from "@/pages/FinModelInterview";
import PitchNew from "@/pages/PitchNew";
import PitchEditor from "@/pages/PitchEditor";
import PitchPublic from "@/pages/PitchPublic";
import DeckPrint from "@/pages/DeckPrint";
import MgmtNew from "@/pages/MgmtNew";
import MgmtEditor from "@/pages/MgmtEditor";
import Admin from "@/pages/Admin";
import CreditsPacks from "@/pages/CreditsPacks";
import PaymentCallback from "@/pages/PaymentCallback";
import ValuationNew from "@/pages/ValuationNew";
import ValuationBuilder from "@/pages/ValuationBuilder";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-stone-500 font-display" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

/* Catches uncaught render errors anywhere in the tree so a single broken
   component can never blank the whole page. */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("Uncaught render error:", error, info?.componentStack);
    // Report the crash so admins can see which page broke for which founder.
    import("@/lib/api").then(({ api }) => api.post("/client-errors", {
      message: String(error?.message || error || "").slice(0, 500),
      stack: String(error?.stack || "").slice(0, 4000),
      component_stack: String(info?.componentStack || "").slice(0, 4000),
      path: window.location.pathname + window.location.search,
      user_agent: navigator.userAgent,
    })).catch(() => { /* reporting must never crash the fallback */ });
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen grid place-items-center px-6" style={{ backgroundColor: "#050E0D" }} data-testid="app-error-boundary">
          <div className="max-w-md text-center">
            <div className="text-2xl font-extrabold text-white">Something went wrong</div>
            <p className="mt-2 text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
              An unexpected error stopped this page from rendering. Your data is safe — retry, or head back to your dashboard.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button data-testid="error-boundary-retry"
                onClick={() => this.setState({ error: null })}
                className="px-5 py-2.5 rounded-lg font-bold text-sm"
                style={{ backgroundColor: "#0A8B85", color: "#fff" }}>
                Try again
              </button>
              <button data-testid="error-boundary-home"
                onClick={() => { window.location.href = "/dashboard"; }}
                className="px-5 py-2.5 rounded-lg font-bold text-sm"
                style={{ border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Redirect direct URLs for locked services (Management
   Accounts, Business Valuation) back to the dashboard in production so
   founders end up on the tiles where the "Join the shortlist" gate opens.
   Preview keeps the pages fully accessible for continued development. */
function LockedInProduction({ children }) {
  if (isProduction()) {
    return <Navigate to="/dashboard?locked=1" replace />;
  }
  return children;
}

/* Lets non-React modules (e.g. the api.js 402 interceptor) trigger SPA
   navigation via `window.dispatchEvent(new CustomEvent("clinq:navigate",
   { detail: "/credits" }))` without a full page reload. */
function NavigateBridge() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const handler = (e) => { if (typeof e.detail === "string") navigate(e.detail); };
    window.addEventListener("clinq:navigate", handler);
    return () => window.removeEventListener("clinq:navigate", handler);
  }, [navigate]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/sso" element={<SSOCallback />} />
      <Route path="/build/start" element={<BuildStart />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/fm/new" element={<ProtectedRoute><FinModelNew /></ProtectedRoute>} />
      <Route path="/fm/:fmId/interview" element={<ProtectedRoute><FinModelInterview /></ProtectedRoute>} />
      <Route path="/fm/:fmId" element={<ProtectedRoute><FinModelBuilder /></ProtectedRoute>} />
      <Route path="/pitch/new" element={<ProtectedRoute><PitchNew /></ProtectedRoute>} />
      <Route path="/pitch/:deckId" element={<ProtectedRoute><PitchEditor /></ProtectedRoute>} />
      <Route path="/p/:deckId" element={<PitchPublic />} />
      <Route path="/deck-print" element={<DeckPrint />} />
      <Route path="/mgmt/new" element={<ProtectedRoute><LockedInProduction><MgmtNew /></LockedInProduction></ProtectedRoute>} />
      <Route path="/mgmt/:accountId" element={<ProtectedRoute><LockedInProduction><MgmtEditor /></LockedInProduction></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><CreditsPacks /></ProtectedRoute>} />
      <Route path="/payment/callback" element={<ProtectedRoute><PaymentCallback /></ProtectedRoute>} />
      <Route path="/valuation/new" element={<ProtectedRoute><LockedInProduction><ValuationNew /></LockedInProduction></ProtectedRoute>} />
      <Route path="/valuation/:valuationId" element={<ProtectedRoute><LockedInProduction><ValuationBuilder /></LockedInProduction></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return (
    <div className="App">
      <HelmetProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          <BrowserRouter>
            <AuthProvider>
              <ErrorBoundary>
                <NavigateBridge />
                <TopUpModal />
                <AppRouter />
                <Toaster position="bottom-right" />
              </ErrorBoundary>
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </div>
  );
}
